import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Entity } from '@shared/data/entity/Entity';
import { createCtx } from '@shared/util/context';
import { useBoolean } from '@shared/util/hooks';
import { MAX_SELECTED_ENTITIES } from './constants';

interface EntitySelectionContextValue {
  selectedEntities: Entity[];
  selectedCount: number;
  addEntity(entity: Entity): void;
  removeEntity(id: string): void;
  toggleEntity(entity: Entity): void;
  isEntitySelected(id: string): boolean;
  selectionActive: boolean;
  reset(): void;
  loading: boolean;
  startLoading(): void;
  stopLoading(): void;
}

const [useEntitySelection, ContextProvider] = createCtx<EntitySelectionContextValue>();

export { useEntitySelection };

export function EntitySelectionProvider({ children }: { children: ReactNode }) {
  const [selectedEntities, setSelectedEntities] = useState<Entity[]>([]);
  const [loading, startLoading, stopLoading] = useBoolean();

  const addEntity = useCallback((entity: Entity) => {
    setSelectedEntities(prev => {
      if (prev.length >= MAX_SELECTED_ENTITIES) {
        return prev;
      }
      return [...prev.filter(item => item.id !== entity.id), entity];
    });
  }, []);

  const removeEntity = useCallback((id: string) => {
    setSelectedEntities(prev => prev.filter(item => item.id !== id));
  }, []);

  const reset = useCallback(() => {
    setSelectedEntities([]);
    stopLoading();
  }, [stopLoading]);

  const isEntitySelected = useCallback(
    (id: string) => {
      return selectedEntities.some(item => item.id === id);
    },
    [selectedEntities],
  );

  const toggleEntity = useCallback(
    (entity: Entity) => {
      if (isEntitySelected(entity.id)) {
        removeEntity(entity.id);
      } else {
        addEntity(entity);
      }
    },
    [addEntity, removeEntity, isEntitySelected],
  );

  const value = useMemo(() => {
    const selectedCount = selectedEntities.length;
    const selectionActive = selectedCount > 0;

    return {
      selectedEntities,
      selectedCount,
      selectionActive,
      addEntity,
      removeEntity,
      toggleEntity,
      isEntitySelected,
      reset,
      loading,
      startLoading,
      stopLoading,
    };
  }, [
    selectedEntities,
    addEntity,
    removeEntity,
    toggleEntity,
    reset,
    loading,
    startLoading,
    stopLoading,
    isEntitySelected,
  ]);

  return <ContextProvider value={value}>{children}</ContextProvider>;
}
